import React, { useState, useCallback, useEffect } from 'react';
import { LoadingIcon, SimpleModal } from 'app/common/components';
import {
  useFormatMessage,
  useCustomerAuroraPayInvoice
} from 'app/common/hooks';
import messages from './CustomerAuroraPayInvoice.messages.js';
import * as RequestStatus from '../../../hooks/useRestApi/RequestStatus.js';

const CustomerAuroraPayInvoice = ({
  orderId,
  invoiceRefIds = [],
  isReturns = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  return (
    <>
      <div className={'mt-2'}>
        {invoiceRefIds.length > 0 ? (
          invoiceRefIds.map((invoiceRefId, index) => (
            <AuroraPayInvoiceLink
              key={invoiceRefId || index}
              myKey={invoiceRefId || index}
              index={index}
              invoiceRefId={invoiceRefId}
              orderId={orderId}
              isReturns={isReturns}
              setIsModalOpen={setIsModalOpen}
              setPdfData={setPdfData}
            />
          ))
        ) : (
          <span className="text-sm text-gray-500">No invoices available</span>
        )}
      </div>

      {isModalOpen && (
        <SimpleModal
          isOpen={isModalOpen}
          onClose={() => {
            // Clean up the object URL to prevent memory leaks
            if (pdfData) {
              URL.revokeObjectURL(pdfData);
            }
            setIsModalOpen(false);
          }}
          title="AuroraPay Invoice"
          size="xl"
        >
          <div style={{ height: '80vh', width: '100%' }}>
            <object
              data={pdfData}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <p>Unable to display PDF.</p>
            </object>
          </div>
        </SimpleModal>
      )}
    </>
  );
};

const AuroraPayInvoiceLink = ({
  myKey,
  index,
  invoiceRefId,
  orderId,
  isReturns,
  setIsModalOpen,
  setPdfData
}) => {
  const formatMessage = useFormatMessage();

  const {
    requestStatus,
    loading: isLoading,
    response,
    sendCallback: doFetchInvoicePdf
  } = useCustomerAuroraPayInvoice({}, orderId, invoiceRefId, isReturns, false);

  const fetchInvoice = useCallback(
    async (e, invoiceRefId) => {
      e.preventDefault();
      if (requestStatus === RequestStatus.IN_PROGRESS) {
        return;
      }
      try {
        // Use the sendCallback from the hook
        await doFetchInvoicePdf();
      } catch (error) {
        console.error('Error fetching invoice:', error);
      }
    },
    [requestStatus, doFetchInvoicePdf]
  );

  useEffect(() => {
    if (requestStatus === RequestStatus.COMPLETE && response && invoiceRefId) {
      try {
        // The response from useRestApi is already the data property of the original response
        const dataUrl = convertToBlob(response);
        setPdfData(dataUrl);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Error processing PDF data:', error);
      }
    }
  }, [requestStatus, response, invoiceRefId, setIsModalOpen, setPdfData]);

  return (
    <a
      key={myKey}
      href="#"
      onClick={e => fetchInvoice(e, invoiceRefId)}
      className="inline-flex items-center justify-center px-3 py-2 mr-4 text-sm font-semibold text-primary-600 bg-white border border-primary-200 rounded hover:bg-primary-100 hover:text-primary-700 focus:bg-primary-100 focus:text-primary-700 focus:outline-none"
    >
      {isLoading && invoiceRefId === invoiceRefId ? (
        <LoadingIcon className={'mr-2'} />
      ) : null}
      {formatMessage(isReturns ? messages.creditInvoice : messages.invoice)} #
      {index + 1}
    </a>
  );
};

function convertToBlob(data) {
  const binaryData = convertBase64ToBinary(data);
  const blob = new Blob([binaryData], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

/**
 * Converts base64 encoded bytes to binary data suitable for a Blob
 * @param {string} base64String - The base64 encoded string
 * @returns {Uint8Array} The binary data
 */
const convertBase64ToBinary = base64String => {
  try {
    const byteCharacters = atob(base64String);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return byteArray;
  } catch (error) {
    console.error('Error converting base64 to binary:', error);
    return new Uint8Array();
  }
};

export default CustomerAuroraPayInvoice;
export { CustomerAuroraPayInvoice };
